import React from 'react';
import {
  RiBuildingLine,
  RiFocus3Line,
  RiMenuLine,
  RiSearchLine,
  RiBookOpenLine,
} from '@remixicon/react';
import type { IconComponent } from '@backstage/core-plugin-api';
import { LogoIcon } from '../../shared/components/logo';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { SearchModal } from '../../features/search/components';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../i18n';
import styles from './root.layout.module.css';
import type {
  NavContentComponent,
  NavContentNavItem,
} from '@backstage/plugin-app-react';
import { Flex } from '@backstage/ui';

const SidebarNavItemInner = ({ href, title, icon }: NavContentNavItem) => {
  const { t } = useTranslationRef(appTranslationRef);
  const translationMap: Record<string, string> = {
    Catalog: t('sidebar.catalog'),
    Docs: t('sidebar.docs'),
    Create: t('sidebar.create'),
    Settings: t('sidebar.settings'),
  };
  const text = translationMap[title] ?? title;
  return <SidebarItem icon={() => icon} to={href} text={text} />;
};

const SidebarLogo = () => {
  const { isOpen } = useSidebarOpenState();
  const { t } = useTranslationRef(appTranslationRef);

  return (
    <Flex
      px="4"
      className={styles.sidebarLogoRoot}
      style={
        {
          '--sidebar-drawer-width-closed': `${sidebarConfig.drawerWidthClosed}px`,
          '--sidebar-logo-height': `${sidebarConfig.logoHeight}px`,
        } as React.CSSProperties
      }
    >
      <Link
        to="/"
        underline="none"
        className={isOpen ? styles.sidebarLogoLinkOpen : styles.sidebarLogoLink}
        aria-label={t('sidebar.home')}
      >
        <LogoIcon compact />
      </Link>
    </Flex>
  );
};

export const RootLayout: NavContentComponent = ({ navItems }) => {
  const { t } = useTranslationRef(appTranslationRef);
  const items = navItems.withComponent(SidebarNavItemInner);

  return (
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup
        label={t('sidebar.search')}
        icon={<RiSearchLine />}
        to="/search"
      >
        <SearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label={t('sidebar.menu')} icon={<RiMenuLine />}>
        {items.take('page:catalog')}
        <SidebarItem
          icon={RiBuildingLine as IconComponent}
          to="clients"
          text={t('sidebar.clients')}
        />
        {items.take('page:techdocs')}
        {items.take('page:scaffolder')}
        <SidebarDivider />
        <SidebarScrollWrapper>
          <SidebarItem
            icon={RiFocus3Line as IconComponent}
            to="tech-radar"
            text={t('sidebar.techRadar')}
          />
          <SidebarItem
            icon={RiBookOpenLine as IconComponent}
            to="docs/default/component/kpc-developer-portal"
            text={t('sidebar.guide')}
          />
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarGroup
        label={t('sidebar.settings')}
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        {items.take('page:user-settings')}
      </SidebarGroup>
    </Sidebar>
  );
};
